import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';

import { cn } from '../../lib/utils.ts';

type SliderProps = React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
  /** Show a value bubble above each thumb on hover/drag (replaces MUI's valueLabelDisplay). */
  showValueLabel?: boolean;
  /** Format the value shown in the bubble. Defaults to the raw number. */
  formatValueLabel?: (value: number) => React.ReactNode;
};

const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  ({ className, showValueLabel, formatValueLabel, ...props }, ref) => {
    const value = (props.value ?? props.defaultValue ?? [0]) as number[];

    return (
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          'group/slider relative flex w-full touch-none select-none items-center data-[disabled]:cursor-not-allowed',
          className
        )}
        {...props}
      >
        <SliderPrimitive.Track className="relative h-0.5 w-full grow overflow-hidden rounded-full bg-gray-200">
          <SliderPrimitive.Range className="absolute h-full bg-brand group-data-[disabled]/slider:bg-gray-300" />
        </SliderPrimitive.Track>
        {value.map((v, i) => (
          <SliderPrimitive.Thumb
            key={i}
            className="group/thumb relative z-10 block h-3 w-3 rounded-full border-2 border-brand bg-white shadow-sm outline-none transition-shadow hover:shadow-[0_0_0_6px_color-mix(in_oklab,var(--hs-brand)_16%,transparent)] focus-visible:shadow-[0_0_0_8px_color-mix(in_oklab,var(--hs-brand)_16%,transparent)] group-data-[disabled]/slider:border-gray-300 group-data-[disabled]/slider:pointer-events-none group-data-[disabled]/slider:shadow-none"
          >
            {showValueLabel && (
              <span className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-1.5 py-0.5 text-xs text-white opacity-0 transition-opacity group-hover/thumb:opacity-100 group-focus/thumb:opacity-100">
                {formatValueLabel ? formatValueLabel(v) : v}
              </span>
            )}
          </SliderPrimitive.Thumb>
        ))}
      </SliderPrimitive.Root>
    );
  }
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
